/**
 * Audio review page (dev/QA, 2026-06-12) — lists every Unit 1 audio clip with
 * the SAME audio engine the lessons use (useAudio + AudioButton, Web Audio via
 * engine.ts). Lessons play on Tornike's device, so this does too — unlike the
 * static /voice-clear-pilot.html which used plain <audio>. Temporary; remove
 * after the audio review.
 *
 * Route: /audio-review (public, no auth).
 */
import { useMemo } from 'react';
import { AudioButton } from '@fluent/ui';
import type { Lesson } from '@fluent/types';
import { useAudio } from '../audio/audioPlayer.js';
import { getUnit, getSkill } from '../content/course.js';
import { getLesson } from '../content/lessons.js';
import { phraseChoiceClips } from '../lesson/phraseChoiceText.js';

interface Clip {
  key: string;
  en: string;
  ka: string;
}

function clipsOf(lesson: Lesson): Clip[] {
  const out: Clip[] = [];
  for (const ex of lesson.exercises) {
    if (ex.type === 'tap_pairs') {
      for (const p of ex.pairs) if (p.audio_key) out.push({ key: p.audio_key, en: p.en, ka: p.ka });
    } else if (ex.type === 'phrase_intro') {
      out.push({ key: ex.audio_key, en: ex.en, ka: ex.ka });
      if (ex.example_audio_key && ex.example_en) {
        out.push({ key: ex.example_audio_key, en: ex.example_en, ka: ex.example_ka ?? '' });
      }
    } else if (ex.type === 'dialogue') {
      for (const l of ex.lines) out.push({ key: l.audio_key, en: l.en, ka: l.ka });
    } else if (ex.type === 'listen_type' || ex.type === 'listen_choose') {
      out.push({ key: ex.audio_key, en: ex.answer, ka: ex.translation_ka ?? '' });
    } else if (ex.type === 'translate_typed' || ex.type === 'choose_correct') {
      if (ex.audio_key) out.push({ key: ex.audio_key, en: ex.answer, ka: ex.prompt ?? '' });
    } else if (ex.type === 'cloze') {
      if (ex.audio_key) out.push({ key: ex.audio_key, en: ex.prompt_en.replace('___', ex.answer), ka: ex.prompt_ka });
    } else if (ex.type === 'phrase_choice') {
      for (const c of phraseChoiceClips(ex)) out.push({ key: c.key, en: c.en, ka: c.ka });
    }
  }
  return out;
}

function ClipRow({ clip }: { clip: Clip }) {
  const audio = useAudio(clip.key);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
      <AudioButton
        size="md"
        playing={audio.playing}
        onClick={() => (audio.playing ? audio.stop() : audio.play())}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, fontSize: 15 }}>{clip.en}</span>
        <span style={{ display: 'block', color: '#8a7f6e', fontSize: 12 }}>{clip.ka}</span>
      </div>
      {audio.error && <span style={{ color: '#c8372e', fontWeight: 700 }}>⚠ retry</span>}
    </div>
  );
}

interface Section {
  pos: number;
  title: string;
  rows: Clip[];
}

export function AudioReview() {
  const sections = useMemo<Section[]>(() => {
    const unit = getUnit('unit-01-basics');
    const seen = new Set<string>();
    const secs: Section[] = [];
    let pos = 0;
    for (const skillId of unit?.skills ?? []) {
      const skill = getSkill(skillId);
      for (const lessonId of skill?.lessons ?? []) {
        pos += 1;
        const lesson = getLesson(lessonId);
        if (!lesson) continue;
        const rows: Clip[] = [];
        for (const c of clipsOf(lesson)) {
          if (!seen.has(c.key)) {
            seen.add(c.key);
            rows.push(c);
          }
        }
        if (rows.length) {
          secs.push({ pos, title: `გაკვეთილი ${pos} · ${skill?.title_en ?? ''} — ${lesson.title_en}`, rows });
        }
      }
    }
    return secs;
  }, []);

  const total = sections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div
      style={{
        fontFamily: 'inherit',
        background: '#fff8ee',
        color: '#2b241b',
        minHeight: '100vh',
        padding: '16px 14px 56px',
        maxWidth: 600,
        marginInline: 'auto',
      }}
    >
      <h1 style={{ fontSize: 19 }}>🔊 Unit 1 — ხმები ({total})</h1>
      <p style={{ color: '#6f6458', fontSize: 13, margin: '0 0 16px' }}>
        იგივე ხმა რაც გაკვეთილებში. დააჭირე ▶-ს. რომელი ჟღერს ცუდად — დამიწერე English ტექსტი.
      </p>

      <div style={{ background: '#eef6ff', borderRadius: 14, padding: '12px 14px', marginBottom: 16, border: '1px solid #cfe2fb' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1f5fae', marginBottom: 4 }}>
          🤖 AI ხმის ტესტი (ElevenLabs) — შენი ხმა vs AI
        </div>
        <div style={{ fontSize: 12, color: '#5a6a7a', marginBottom: 10 }}>
          „შენი“ = შენი ჩაწერილი. დანარჩენი = AI. თუ AI საკმარისად კარგია, დანარჩენ ყველაფერს მე გავაკეთებ — ჩაწერა აღარ მოგიწევს.
        </div>
        {[
          { key: 'a1-u01-s01-where-are-you-from.mp3', en: 'Where are you from?', ka: '👤 შენი ხმა' },
          { key: 'pilot-ai-roman-where.mp3', en: 'Where are you from?', ka: '🤖 AI — Roman' },
          { key: 'pilot-ai-sterling-where.mp3', en: 'Where are you from?', ka: '🤖 AI — Sterling' },
          { key: 'a1-u01-s01-hello-i-am-giorgi-v2.mp3', en: 'Hello, I am Giorgi.', ka: '👤 შენი ხმა' },
          { key: 'pilot-ai-roman-giorgi.mp3', en: 'Hello, I am Giorgi.', ka: '🤖 AI — Roman' },
          { key: 'pilot-ai-sterling-giorgi.mp3', en: 'Hello, I am Giorgi.', ka: '🤖 AI — Sterling' },
          { key: 'a1-u01-s01-my-name-is-mariam.mp3', en: 'My name is Mariam.', ka: '👤 შენი ხმა (ქალი)' },
          { key: 'pilot-ai-gia-mariam.mp3', en: 'My name is Mariam.', ka: '🤖 AI — Gia' },
          { key: 'pilot-ai-maya-mariam.mp3', en: 'My name is Mariam.', ka: '🤖 AI — Maya' },
        ].map((c) => (
          <ClipRow key={c.key} clip={c} />
        ))}
        <div style={{ fontSize: 12, color: '#8a7f6e', marginTop: 8 }}>
          მითხარი: AI საკმარისად კარგია? თუ კი — რომელი ხმა (Roman/Sterling კაცი, Gia/Maya ქალი)?
        </div>
      </div>
      {sections.map((s) => (
        <div
          key={s.pos}
          style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 12, boxShadow: '0 2px 8px rgba(43,36,27,.07)' }}
        >
          <div
            style={{ fontSize: 13, fontWeight: 800, color: '#4a9e00', marginBottom: 8, borderBottom: '1px solid #f0e8d8', paddingBottom: 6 }}
          >
            {s.title}
          </div>
          {s.rows.map((c) => (
            <ClipRow key={c.key} clip={c} />
          ))}
        </div>
      ))}
    </div>
  );
}
